<template>
  <div class="space-y-6">
    <p v-if="showHeading" class="text-sm font-medium text-gray-700">
      Permissions
    </p>

    <div v-for="option in visibleTiers" :key="option.value" class="space-y-2">
      <div class="flex items-start gap-3">
        <RadioButton
          v-model="tier"
          :input-id="`tier-${option.value}`"
          :value="option.value"
          :disabled="option.value === 'mobile' && adminDisabled"
          :data-testid="`tier-${option.value}`"
        />
        <label
          :for="`tier-${option.value}`"
          class="flex flex-col cursor-pointer"
        >
          <span class="text-sm font-medium text-gray-700">{{
            option.label
          }}</span>
          <span class="text-xs text-gray-500">{{ option.description }}</span>
        </label>
      </div>
      <p
        v-if="option.value === 'admin' && instructorNote"
        class="ml-8 inline-block rounded bg-green-50 px-2 py-1 text-xs text-green-800"
        data-testid="instructor-note"
      >
        Can manage their own bookings, since they're an
        <strong>instructor</strong>.
      </p>
    </div>

    <Message v-if="showNetworkWarning" severity="warn" :closable="false">
      <div class="text-sm">
        <p class="font-medium">
          Since the organization you selected is a <strong>network</strong>,
          this user will automatically be an <strong>admin</strong>.
        </p>
        <p class="mt-1">
          They will be able to control all child clubs/network.
        </p>
      </div>
    </Message>
  </div>
</template>

<script setup lang="ts">
/**
 * The three cumulative staff tiers (MAI-4955), one radio group:
 * Mobile → Mobile + admin → Mobile + admin + payments. Every tier includes
 * mobile access, so picking any tier turns the coach record on.
 */
import { computed } from "vue";
import RadioButton from "primevue/radiobutton";
import Message from "primevue/message";

type Tier = "mobile" | "admin" | "payments";

const props = defineProps<{
  /** Network members are always admins: the Mobile-only tier is locked. */
  adminDisabled?: boolean;
  showNetworkWarning?: boolean;
  /** Basic admins cannot grant payments; hide the tier instead of 403ing. */
  hidePayments?: boolean;
  /** The user is an instructor: say what Mobile + admin lets them do. */
  instructorNote?: boolean;
  /** The create drawer labels the group; the Permissions tab already is one. */
  showHeading?: boolean;
}>();

const mobile = defineModel<boolean>("mobile", { default: false });
const admin = defineModel<boolean>("admin", { default: false });
const payments = defineModel<boolean>("payments", { default: false });

const TIERS: { value: Tier; label: string; description: string }[] = [
  {
    value: "mobile",
    label: "Mobile",
    description: "Can use staff tools in the mobile app",
  },
  {
    value: "admin",
    label: "Mobile + admin",
    description:
      "Can access the mobile app and the admin site, except payments tools.",
  },
  {
    value: "payments",
    label: "Mobile + admin + payments",
    description:
      "Can access the mobile app and the admin site, including payments tools.",
  },
];

const visibleTiers = computed(() =>
  props.hidePayments ? TIERS.filter((t) => t.value !== "payments") : TIERS
);

const tier = computed<Tier>({
  get: () => (payments.value ? "payments" : admin.value ? "admin" : "mobile"),
  set: (value) => {
    mobile.value = true;
    admin.value = value !== "mobile";
    payments.value = value === "payments";
  },
});
</script>
